<!-- Preview Mode -->
<div *ngIf="mode === 'preview'" [ngStyle]="getPreviewStyles()" class="prose max-w-none p-6 rounded-lg">
  <div *ngIf="getHeading()" class="mb-6">
    <h2 [style.color]="getHeadingColor()" [ngClass]="getTitlePositionClass()" class="text-3xl font-bold">{{ getHeading() }}</h2>
  </div>

  <!-- No Cells State -->
  <div *ngIf="!hasCells()" class="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
    <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
    </svg>
    <h3 class="mt-2 text-sm font-medium text-gray-900">Geen grid cellen</h3>
    <p class="mt-1 text-sm text-gray-500">Voeg cellen toe via de sidebar om componenten te plaatsen.</p>
  </div>

  <!-- Grid with Cells -->
  <div *ngIf="hasCells()" class="grid" [ngStyle]="getGridStyles()">
    <div *ngFor="let cell of getCells(); let i = index"
         [ngStyle]="getGridItemStyles(cell)"
         [class]="isCellSelected(i) && isSelected ? 'ring-2 ring-indigo-500 ring-offset-2' : ''"
         class="min-h-[100px] relative"
         (click)="selectCell(i, $event)"
         style="position: relative;">

      <!-- Cell Index Badge (visible when grid is selected) -->
      <div *ngIf="isSelected"
           class="absolute -top-2 -left-2 w-6 h-6 bg-indigo-600 text-white text-xs rounded-full flex items-center justify-center font-medium z-10">
        {{ i + 1 }}
      </div>

      <!-- Empty Cell State -->
      <div *ngIf="!hasCellComponents(cell)"
           class="flex flex-col items-center justify-center h-full min-h-[80px] text-gray-400">
        <svg class="w-8 h-8 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
        </svg>
        <span class="text-xs">Lege cel</span>
      </div>

      <!-- Cell Components -->
      <div *ngIf="hasCellComponents(cell)" class="space-y-4">
        <div *ngFor="let comp of getCellComponents(cell); let j = index"
             class="relative"
             style="position: relative;">
          
          <!-- Component wrapper with click handler -->
          <div (click)="selectComponentInCell(i, j, $event)" class="relative" style="position: relative; z-index: 1;">

          <!-- Render full components based on type -->
          <ng-container [ngSwitch]="comp.type">
            <!-- Text Component -->
            <app-text-component
              *ngSwitchCase="'text'"
              [component]="comp"
              [mode]="mode"
              [isSelected]="false"
              mode="preview">
            </app-text-component>

            <!-- Media Gallery Component -->
            <app-media-gallery-component
              *ngSwitchCase="'media-gallery'"
              [component]="comp"
              [mode]="mode"
              [isSelected]="false"
              mode="preview">
            </app-media-gallery-component>

            <!-- Products Component -->
            <app-products-component
              *ngSwitchCase="'products'"
              [component]="comp"
              [mode]="mode"
              [isSelected]="false"
              [currentLocale]="currentLocale"
              mode="preview">
            </app-products-component>

            <!-- News Component -->
            <app-news-component
              *ngSwitchCase="'news'"
              [component]="comp"
              [mode]="mode"
              [isSelected]="false"
              [currentLocale]="currentLocale"
              [domainOverride]="domainOverride"
              mode="preview">
            </app-news-component>

            <!-- Projects Component -->
            <app-projects-component
              *ngSwitchCase="'projects'"
              [component]="comp"
              [isSelected]="false"
              [currentLocale]="currentLocale"
              mode="preview">
            </app-projects-component>

            <!-- Form Component -->
            <app-form-component
              *ngSwitchCase="'form'"
              [component]="comp"
              [isSelected]="false"
              mode="preview">
            </app-form-component>

            <!-- Map Component -->
            <app-map-component
              *ngSwitchCase="'map'"
              [component]="comp"
              [isSelected]="false"
              mode="preview">
            </app-map-component>

            <!-- Action Component -->
            <div *ngSwitchCase="'action'" class="py-4 pointer-events-none" [style.text-align]="comp.data?.['alignment'] || 'center'">
              <ng-container [ngSwitch]="comp.data?.['variant'] || 'button'">
                <!-- Button variant -->
                <a *ngSwitchCase="'button'"
                   [href]="comp.data?.['link'] || '#'"
                   class="inline-flex items-center justify-center rounded-lg font-semibold transition-colors bg-indigo-600 text-white hover:bg-indigo-700"
                   [ngClass]="{
                     'px-4 py-2 text-sm': comp.data?.['buttonSize'] === 'sm',
                     'px-6 py-3 text-base': comp.data?.['buttonSize'] === 'md' || !comp.data?.['buttonSize'],
                     'px-8 py-4 text-lg': comp.data?.['buttonSize'] === 'lg'
                   }">
                  {{ comp.data?.['text'] || 'Klik hier' }}
                </a>
                <!-- Banner variant -->
                <div *ngSwitchCase="'banner'"
                     class="relative rounded-lg overflow-hidden"
                     [style.background-color]="comp.data?.['bannerBackgroundColor'] || '#1e40af'"
                     [style.padding-top.px]="comp.data?.['paddingY'] || 48"
                     [style.padding-bottom.px]="comp.data?.['paddingY'] || 48">
                  <div class="relative z-10 text-center px-6">
                    <h3 class="text-2xl font-bold mb-2" [style.color]="comp.data?.['bannerTextColor'] || '#ffffff'">{{ comp.data?.['bannerTitle'] || 'Banner Titel' }}</h3>
                    <p *ngIf="comp.data?.['bannerSubtitle']" class="mb-4" [style.color]="comp.data?.['bannerTextColor'] || '#ffffff'">{{ comp.data?.['bannerSubtitle'] }}</p>
                    <a [href]="comp.data?.['link'] || '#'" class="inline-block px-6 py-3 bg-white text-gray-900 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
                      {{ comp.data?.['text'] || 'Klik hier' }}
                    </a>
                  </div>
                </div>
                <!-- CTA variant -->
                <div *ngSwitchCase="'cta'" class="text-center">
                  <a [href]="comp.data?.['link'] || '#'"
                     class="inline-flex items-center justify-center px-8 py-4 text-lg font-bold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition-colors shadow-lg">
                    {{ comp.data?.['text'] || 'Klik hier' }}
                  </a>
                  <a *ngIf="comp.data?.['secondaryText']"
                     [href]="comp.data?.['secondaryLink'] || '#'"
                     class="inline-flex items-center justify-center px-6 py-3 text-sm font-medium text-gray-300 hover:text-white transition-colors ml-4">
                    {{ comp.data?.['secondaryText'] }}
                  </a>
                </div>
                <!-- Default fallback -->
                <a *ngSwitchDefault
                   [href]="comp.data?.['link'] || '#'"
                   class="inline-flex items-center justify-center px-6 py-3 rounded-lg font-semibold bg-indigo-600 text-white hover:bg-indigo-700 transition-colors">
                  {{ comp.data?.['text'] || 'Klik hier' }}
                </a>
              </ng-container>
            </div>

            <!-- Default/Unknown -->
            <div *ngSwitchDefault class="text-sm text-gray-400 italic">
              {{ comp.type }} component
            </div>
          </ng-container>
          </div>
        </div>
      </div>

    </div>
  </div>

  <!-- Empty Grid Preview (when no cells but grid is selected, show placeholder cells) -->
  <div *ngIf="!hasCells() && isSelected" class="grid mt-4" [ngStyle]="getGridStyles()">
    <div *ngFor="let i of [1,2,3,4,5,6].slice(0, getColumns())"
         [ngStyle]="getGridItemStyles()"
         class="h-24 flex items-center justify-center text-gray-400 text-sm border-2 border-dashed border-gray-300">
      Cel {{ i }}
    </div>
  </div>
</div>

<!-- Edit Mode -->
<div *ngIf="mode === 'edit'">
  <p class="text-sm text-gray-400">Gebruik de sidebar om grid cellen en componenten aan te passen.</p>
</div>
